'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { isEmpty, isNil } from 'lodash'
import { motion, useReducedMotion } from 'framer-motion'

import { IndustryT } from '@/lib/types/CompanyT'
import { DEFAULT_VCARD_BANNER, getVCardSocial } from '@/constants/VCard'
import { INDUSTRY_CONFIG, INDUSTRY_SUGGESTED_ACTION_LINKS } from '@/constants/company'
import { VCardFileLink, VCardPreviewType } from '@/lib/types/VCardT'
import { cn, formatPhoneNumber, getWordInitials, isNullOrEmpty } from '@/lib/utils'
import { formatFileSize, getFileIcon } from '@/lib/helpers/fileHelpers'
import { buildAddressString } from '@/lib/helpers/buildAddressString'
import { DEFAULT_BRAND, brandVars } from '@/lib/color/brand'
import { useScrollToNewItem } from '@/lib/hooks/useScrollToNewItem'
import { IconTypes } from '@/components/Icon'
import Image from '@/components/Image'
import LcIcon from '@/components/livecontact/icons'
import { SocialTile } from '@/components/livecontact/SocialGlyph'
import Tile from '@/components/livecontact/Tile'
import StatusChip from '@/components/livecontact/StatusChip'
import CopyButton from '@/components/livecontact/CopyButton'
import { ClampText } from '@/components/livecontact/Collapsible'
import { EASE_OUT, fadeUpVariants, staggerContainer } from '@/components/livecontact/motion'
import ActionItem from '@/components/ContactLinkCard/components/ActionItem'
import AddressLine from '@/components/ContactLinkCard/components/AddressLine'
import LinkPreviewSkeleton from '@/components/ContactLinkCard/components/LinkPreviewSkeleton'

type Props = {
  data: VCardPreviewType | null
  isLoading?: boolean
  classInner?: string
}

type Variants = typeof fadeUpVariants

/**
 * Brand-coloured bed behind the banner image — it fills the frame while the image decodes and
 * shows through a banner with transparency.
 */
const HERO_BACKGROUND = [
  'radial-gradient(120% 120% at 0% 0%, rgba(255,255,255,0.28) 0%, transparent 58%)',
  'linear-gradient(135deg, var(--brand) 0%, color-mix(in srgb, var(--brand) 74%, #fff) 100%)',
].join(', ')

/** Logo plate tint when the card has no uploaded avatar — brand mixed into the card surface. */
const AVATAR_TINT = 'color-mix(in srgb, var(--brand) 18%, rgb(var(--lc-surface)))'

/**
 * Resolves the card's banner to something an <img> can render: a saved URL, an object URL for a
 * banner picked but not yet uploaded, or the banner that ships with the app. Object URLs are
 * revoked when the pick changes so a long editing session doesn't hold every preview in memory.
 */
const useBannerSrc = (banner: VCardPreviewType['banner']) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!(banner instanceof File)) {
      setObjectUrl(null)
      return
    }

    const url = URL.createObjectURL(banner)
    setObjectUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [banner])

  if (banner instanceof File) return objectUrl ?? DEFAULT_VCARD_BANNER
  return banner || DEFAULT_VCARD_BANNER
}

const hostOf = (url: string) => {
  try {
    return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

type CardAction = {
  key: string
  icon?: IconTypes
  glyph?: React.ReactNode
  title: string
  subtitle?: string
  url: string
}

/** 12px meta item under the name: a glyph and a fact we actually hold. */
const MetaItem = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
  <span className="inline-flex min-w-0 items-center gap-1.5 text-[12px] leading-[1.4] tracking-[-0.2px] text-ink-3">
    <span aria-hidden className="inline-flex shrink-0 text-ink-4 [&>svg]:size-3">
      {icon}
    </span>
    <span className="truncate">{children}</span>
  </span>
)

/** Bottom grid cell: bordered icon tile, quiet label, the value, and a copy affordance. */
const ContactCell = ({
  glyph,
  label,
  value,
  href,
  copyValue,
  innerRef,
}: {
  glyph: React.ReactNode
  label: string
  value: string
  href: string
  copyValue: string
  innerRef: (el: HTMLElement | null) => void
}) => (
  <div className="group/cell flex min-w-0 items-center gap-1">
    <a
      ref={innerRef as React.Ref<HTMLAnchorElement>}
      href={href}
      // A small inset so the hover plate carries padding behind the glyph instead of clipping
      // to it.
      className="flex min-h-[44px] min-w-0 flex-1 items-center gap-2.5 rounded-[10px] px-2 py-1 outline-none transition-[background-color,transform] duration-[140ms] ease-out-strong hover:bg-canvas/70 focus-visible:ring-2 focus-visible:ring-brand/40 active:scale-[0.98] dark:hover:bg-surface-2"
    >
      <Tile size="sm" tone="neutral" className="rounded-[8px] bg-surface text-ink-2 shadow-input">
        {glyph}
      </Tile>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12px] leading-[1.4] tracking-[-0.2px] text-ink-4">
          {label}
        </span>
        <span
          title={value}
          className="block truncate text-[13px] font-medium leading-[1.4] tracking-[-0.2px] text-ink"
        >
          {value}
        </span>
      </span>
    </a>
    {/* Pointer devices only: on a phone the tel:/mailto: tap is the useful action, and a 24px
        copy chip would be an undersized tap target next to it. */}
    <CopyButton
      size={24}
      value={copyValue}
      label={`Copy ${label.toLowerCase()}`}
      className="opacity-0 transition-opacity duration-[140ms] group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 md:hidden"
    />
  </div>
)

/**
 * The public digital business card — what a contact sees after a scan, tap or shared link.
 * The brand colour drives the hero gradient, the logo plate and the hero action; everything
 * below the hero is quiet monochrome so the owner's colour is the only colour on the card.
 * Sections fade up in a 40ms stagger when data arrives; nothing blocks interaction.
 */
const ContactCardPreview = ({ data, isLoading, classInner }: Props) => {
  const reduceMotion = useReducedMotion()
  const primaryColor = useMemo(() => data?.mainColor || DEFAULT_BRAND, [data?.mainColor])

  const sectionVariants = useMemo<Variants>(
    () =>
      reduceMotion
        ? {
            hidden: { opacity: 0, y: 0 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.2, ease: EASE_OUT } },
          }
        : fadeUpVariants,
    [reduceMotion]
  )

  /** Every social the card carries, in the order the editor collected them. */
  const validSocials = useMemo(
    () => data?.socialLinks?.filter(({ url, type }) => !isEmpty(url) && Boolean(type)) ?? [],
    [data?.socialLinks]
  )

  const validDefaultLinks = useMemo(
    () =>
      data?.defaultLinks?.filter(({ url, fileId }) => Boolean(url?.trim()) || Boolean(fileId)) ??
      [],
    [data]
  )
  const validUnverifiedPhones = useMemo(
    () => data?.unverifiedPhones?.filter(({ phone }) => Boolean(phone?.trim())) ?? [],
    [data]
  )
  const validEmails = useMemo(
    () => data?.emails?.filter(({ value }) => Boolean(value?.trim())) ?? [],
    [data]
  )
  const validAddresses = useMemo(
    () =>
      data?.addresses?.filter((address) =>
        Boolean(
          address.street1?.trim() ||
          address.street2?.trim() ||
          address.city?.trim() ||
          address.state?.trim() ||
          address.postal?.trim()
        )
      ) ?? [],
    [data?.addresses]
  )
  const fileItems = useMemo(
    () => ((data?.companyFileItems as VCardFileLink[] | undefined) ?? []).filter(Boolean),
    [data?.companyFileItems]
  )
  const keywords = useMemo(
    () => (data?.taxonomy ?? []).filter((word) => Boolean(word?.trim())),
    [data?.taxonomy]
  )

  const fullName = useMemo(() => `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim(), [data])
  const hasRealAvatar = Boolean(data?.avatar) && !data?.isAvatarGenerated
  const industryLabel = data?.industry
    ? INDUSTRY_CONFIG[data.industry as IndustryT]?.label || data.industry
    : ''

  /**
   * Every link the card offers, in one list: the industry action links first (the first of
   * them becomes the hero action), then files, then the website.
   */
  const actions = useMemo<CardAction[]>(() => {
    const items: CardAction[] = []

    validDefaultLinks.forEach(({ label, url, file, clientType }) => {
      const fieldConfig = INDUSTRY_SUGGESTED_ACTION_LINKS[data?.industry as IndustryT]?.find(
        (action) => action.clientType === clientType
      )
      items.push({
        key: `link:${clientType}`,
        icon: (fieldConfig?.icon || 'link') as IconTypes,
        title: label || 'Link',
        url: url || file?.permanentUrl || '',
      })
    })

    fileItems.forEach((fileLink) => {
      items.push({
        key: `file:${fileLink.id}`,
        icon: getFileIcon(fileLink.file?.mimeType, fileLink.file?.type) as IconTypes,
        title: fileLink.name,
        subtitle: formatFileSize(fileLink.file?.size ?? 0),
        url: fileLink.file?.permanentUrl ?? '',
      })
    })

    if (data?.website) {
      items.push({
        key: 'website',
        glyph: <LcIcon name="globe" />,
        title: 'Website',
        subtitle: hostOf(data.website),
        url: data.website,
      })
    }

    return items.filter((item) => Boolean(item.url))
  }, [validDefaultLinks, fileItems, data?.website, data?.industry])

  const [heroAction, ...secondaryActions] = actions

  const trackedItems = useMemo(
    () => [
      ...(data
        ? [
            { id: 'name', value: fullName },
            { id: 'industry', value: data.industry },
            { id: 'representativeName', value: data.representativeName },
            { id: 'avatar', value: [data.avatar, data.isAvatarGenerated] },
            { id: 'brandColor', value: data.mainColor, scrollToTop: true },
          ]
        : []),
      ...(data?.notes ? [{ id: 'notes', value: data.notes }] : []),
      ...(isEmpty(keywords) ? [] : [{ id: 'taxonomy', value: keywords }]),
      ...validSocials.map(({ type, url }) => ({ id: `social:${type}`, value: url })),
      ...validUnverifiedPhones.map(({ phone, label }) => ({ id: `phone:${phone}`, value: label })),
      ...validEmails.map(({ value, alias }) => ({ id: `email:${value}`, value: alias })),
      ...validAddresses.map((address) => ({
        id: `address:${buildAddressString(address)}`,
        value: address.label,
      })),
      ...actions.map(({ key, title, url }) => ({ id: key, value: `${title}|${url}` })),
    ],
    [
      data,
      fullName,
      keywords,
      validSocials,
      validUnverifiedPhones,
      validEmails,
      validAddresses,
      actions,
    ]
  )
  const { registerItem } = useScrollToNewItem(trackedItems)
  const bannerSrc = useBannerSrc(data?.banner)
  const isLocalBanner = bannerSrc.startsWith('blob:') || bannerSrc.startsWith('data:')

  if (isNil(data) || isLoading) {
    return <LinkPreviewSkeleton classInner={classInner} />
  }

  const displayName = fullName || data.representativeName
  const tagline = data.jobPosition || ''
  const hasContactGrid = !isEmpty(validUnverifiedPhones) || !isEmpty(validEmails)
  const hasActions = !isEmpty(actions) || !isNullOrEmpty(validAddresses)

  return (
    <motion.article
      style={brandVars(primaryColor)}
      variants={staggerContainer(0.04)}
      initial="hidden"
      animate="visible"
      className={cn(
        'lc-brand lc-asset-preview relative w-full overflow-hidden rounded-[28px] border border-line bg-surface text-ink shadow-surface',
        classInner
      )}
    >
      {/* Hero banner — the card's own image, or the one that ships with the app. It carries no
          copy: the name reads from the heading below, where it is legible over any photo. */}
      <div
        ref={registerItem('representativeName')}
        className="relative h-[140px] overflow-hidden rounded-t-[28px]"
        style={{ background: HERO_BACKGROUND }}
      >
        {isLocalBanner ? (
          // A banner picked but not yet uploaded is a blob: URL, which next/image can't optimise.
          // eslint-disable-next-line @next/next/no-img-element
          <img
            aria-hidden
            src={bannerSrc}
            alt=""
            className="absolute inset-0 size-full object-cover"
          />
        ) : (
          <Image
            aria-hidden
            src={bannerSrc}
            alt=""
            width={800}
            height={280}
            sizes="(max-width: 440px) 100vw, 400px"
            priority
            className="absolute inset-0 size-full object-cover"
          />
        )}
        {/* A whisper of shade at the foot so the logo plate reads against a pale banner. */}
        <div
          aria-hidden
          className="absolute inset-0 bg-[linear-gradient(180deg,transparent_62%,rgba(0,0,0,0.14))]"
        />
      </div>

      {/* Logo plate, overlapping the banner inside a card-coloured ring */}
      <div className="relative z-10 -mt-12 px-6">
        <div
          ref={registerItem('avatar')}
          className="size-24 rounded-full bg-surface p-1 shadow-[0_2px_10px_rgba(0,0,0,0.14)]"
        >
          <div
            className="flex size-full items-center justify-center overflow-hidden rounded-full"
            style={{ backgroundColor: hasRealAvatar ? 'transparent' : AVATAR_TINT }}
          >
            {hasRealAvatar ? (
              <Image
                src={data.avatar ?? ''}
                alt={`${data.representativeName} logo`}
                width={176}
                height={176}
                className="h-full w-full object-cover"
              />
            ) : (
              <span
                className="text-[24px] font-semibold uppercase leading-none tracking-[-0.4px]"
                style={{ color: 'var(--brand-ink, var(--brand))' }}
              >
                {getWordInitials(displayName)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Identity, description, socials, keywords */}
      <div className="flex flex-col gap-3 px-6 pb-3 pt-3">
        <motion.header variants={sectionVariants} className="flex flex-col gap-px">
          <h2
            ref={registerItem('name')}
            className="text-[22px] font-bold leading-[1.5] tracking-[-0.22px] text-ink [overflow-wrap:anywhere]"
          >
            {displayName}
          </h2>
          {tagline && (
            <p className="text-[13px] leading-[1.5] tracking-[-0.2px] text-ink-3 [overflow-wrap:anywhere]">
              {tagline}
            </p>
          )}
        </motion.header>

        {/* Meta line — only facts the card actually holds */}
        <motion.div
          variants={sectionVariants}
          className={cn(
            'flex w-full flex-wrap items-center gap-x-3 gap-y-1 border-b border-line',
            industryLabel && 'pb-[13px]'
          )}
        >
          {industryLabel && (
            <span ref={registerItem('industry')} className="inline-flex min-w-0">
              <MetaItem icon={<LcIcon name="tags" />}>{industryLabel}</MetaItem>
            </span>
          )}
        </motion.div>

        {data.notes && (
          <motion.section ref={registerItem('notes')} variants={sectionVariants}>
            <ClampText
              lines={4}
              className="[&>div]:whitespace-pre-wrap [&>div]:text-[13px] [&>div]:leading-[1.5] [&>div]:text-ink-2 [&>div]:[overflow-wrap:anywhere]"
            >
              {data.notes}
            </ClampText>
          </motion.section>
        )}

        {!isEmpty(validSocials) && (
          <motion.section
            variants={sectionVariants}
            aria-label="Social profiles"
            className="border-b border-line pb-[13px]"
          >
            {/* 28px brand tiles inside 40px tap targets, pulled tight with a negative margin
                so a dozen networks still read as one compact strip rather than a wall. */}
            <div className="-mx-1.5 -my-1 flex flex-wrap items-center">
              {validSocials.map(({ type, url }) => {
                const field = getVCardSocial(type)
                if (!field) return null

                return (
                  <a
                    key={type}
                    ref={registerItem(`social:${type}`)}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={field.name}
                    title={field.name}
                    className="group/social inline-flex size-10 items-center justify-center rounded-[12px] outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                  >
                    <SocialTile
                      type={type}
                      size={28}
                      radius={9}
                      className="shadow-[0_1px_2px_rgba(0,0,0,0.14)] transition-transform duration-[140ms] ease-out-strong group-hover/social:-translate-y-0.5 group-active/social:scale-[0.92] motion-reduce:transform-none"
                    />
                  </a>
                )
              })}
            </div>
          </motion.section>
        )}

        {!isEmpty(keywords) && (
          <motion.section
            ref={registerItem('taxonomy')}
            variants={sectionVariants}
            aria-label="Keywords"
            className="flex flex-wrap gap-1.5 pt-1"
          >
            {keywords.map((word) => (
              <StatusChip key={word} tone="neutral" dot={false} className="h-6 gap-1 px-2">
                <LcIcon
                  name="check"
                  size={10}
                  strokeWidth={2.4}
                  className="shrink-0 text-success"
                />
                {word}
              </StatusChip>
            ))}
          </motion.section>
        )}
      </div>

      {/* Actions — hero first, then the hairline rows */}
      {hasActions && (
        <motion.section variants={sectionVariants} className="flex flex-col gap-2 px-6 pb-1 pt-0">
          {heroAction && (
            <ActionItem
              primary
              ref={registerItem(heroAction.key)}
              icon={heroAction.icon}
              glyph={heroAction.glyph}
              title={heroAction.title}
              subtitle={heroAction.subtitle}
              url={heroAction.url}
              isExternal
            />
          )}
          {secondaryActions.map((action) => (
            <ActionItem
              key={action.key}
              ref={registerItem(action.key)}
              icon={action.icon}
              glyph={action.glyph}
              title={action.title}
              subtitle={action.subtitle}
              url={action.url}
              isExternal
            />
          ))}
          {validAddresses.map((address, i) => (
            <AddressLine
              key={i}
              address={address}
              ref={registerItem(`address:${buildAddressString(address)}`)}
            />
          ))}
        </motion.section>
      )}
      {/* Phone and email rows — one per line, tucked close under the action links so the two
          read as the card's single "reach me" block. */}
      {hasContactGrid && (
        <motion.section variants={sectionVariants} className="flex flex-col gap-2 px-6 pb-6 pt-1">
          {validUnverifiedPhones.map(({ phone, label }, idx) => (
            <ContactCell
              key={`phone-${idx}`}
              innerRef={registerItem(`phone:${phone}`)}
              glyph={<LcIcon name="call" />}
              label={label || 'Phone'}
              value={formatPhoneNumber(phone)}
              href={`tel:${phone}`}
              copyValue={phone}
            />
          ))}
          {validEmails.map(({ alias, value }, idx) => (
            <ContactCell
              key={`email-${idx}`}
              innerRef={registerItem(`email:${value}`)}
              glyph={<LcIcon name="mail" />}
              label={alias || 'Email'}
              value={value}
              href={`mailto:${value}`}
              copyValue={value}
            />
          ))}
        </motion.section>
      )}
    </motion.article>
  )
}

export default ContactCardPreview
